import { Component, ViewChild, input, effect, output, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { take } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { VaultDocument } from '../../models/vaultDocument';
import { PropertyService } from '../../services/PropertyService';
import { base64ToBlob } from '../../helpers/blob.utils';
import { GeneralDataService } from '../../services/GeneralDataService';
import { AbstractDocument } from '../../models/abstractDocument';

@Component({ standalone: false,
    selector: 'app-documents-table',
    templateUrl: './documents-table.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./documents-table.component.scss']
  })
  export class DocumentsTableComponent {
    readonly vaultDocuments = input<VaultDocument[]|undefined>(undefined);
    readonly auctionId = input<number|undefined>(undefined);
    readonly userCanSeeRestrictedDocuments = input<boolean>(false);
    readonly readOnly = input<boolean>(true);
    readonly canDelete = input<boolean>(false);
    readonly groupedByContext = input<boolean>(false);
    
    readonly deleteDocument = output<VaultDocument>();
    readonly editDocument = output<VaultDocument>();
    readonly showLegalAd = output<VaultDocument|undefined>();
    readonly executeNDA = output<any>();
     
    @ViewChild(MatPaginator, {static: true}) paginator?: MatPaginator;

    public sort?: MatSort;
    public dataSources?: Map<string, MatTableDataSource<AbstractDocument>>;
    private openedDocumentGroups:string[]=[];

    constructor(private propertyService:PropertyService, private generalDataService:GeneralDataService){
      effect(() => {
        this.processVaultDocuments();
      });
      effect(() => {  
        this.processAuctionId();
      });
    }

    get displayedColumns(){
      return ['edited', 'name', 'description', 'type', 'actions'];
    }

    public onGroupingNameClick(key:string){
      //toggle open/close
      if(this.openedDocumentGroups.includes(key)){
        this.openedDocumentGroups = this.openedDocumentGroups.filter(k=>k!=key);
      }
      else{
        this.openedDocumentGroups.push(key);
      }
    }

    public isDocumentGroupOpened(key:string){
      return this.openedDocumentGroups.includes(key);
    }

    public assignSort(key: string, sort: MatSort) {
      const ds = this.dataSources?.get(key);
      if (ds) {
        ds.sort = sort;
      }
    }

    private processVaultDocuments(){
      if(!this.dataSources) this.dataSources =  new Map<string, MatTableDataSource<AbstractDocument>>();
      if(this.vaultDocuments()){
        let dataSource;
        let ds;
        if(!this.groupedByContext()){
          if(!this.dataSources.get('all')){
            this.dataSources.set('all', new MatTableDataSource<AbstractDocument>([]));
          }
          dataSource = this.dataSources.get('all')!;
          ds = dataSource.data.slice(0); //array could already be created vault document processing
          //remove all but the potential legal ad in this array, as it is added by another process
          let legalAd = ds.find(d=>d.typeId==2);
          ds=[];
          if(legalAd) ds.push(legalAd);
          this.vaultDocuments()?.forEach(vd=>{
            ds.push(vd.abstract);
          });
          dataSource.data=ds;
          dataSource.paginator = this.paginator;
          dataSource.sort = this.sort;
          dataSource.sortingDataAccessor=this.vaultDocumentSortingDataAccessor.bind(this);
        }
        else{
          //group by contextHeader
          let contextHeaders = Array.from(new Set(this.vaultDocuments()!.map(vd=>vd.displayContextHeader)));
          for(let ch of contextHeaders){
            if(!this.dataSources.get(ch)){
              this.dataSources.set(ch, new MatTableDataSource<AbstractDocument>([]));
            }
            dataSource = this.dataSources.get(ch)!;
            ds = dataSource.data.slice(0); //array could already be created vault document processing
            //remove all but the potential legal ad in this array, as it is added by another process
            let legalAd = ds.find(d=>d.typeId==2);
            ds=[];
            if(legalAd) ds.push(legalAd);
            this.vaultDocuments()?.filter(vd=>vd.displayContextHeader==ch).forEach(vd=>{
                ds.push(vd.abstract);
            });
            dataSource.data=ds;
            dataSource.paginator = this.paginator;
            dataSource.sort = this.sort;
            dataSource.sortingDataAccessor=this.vaultDocumentSortingDataAccessor.bind(this);
          }
        }
        
      }
    }

    private vaultDocumentSortingDataAccessor(item: AbstractDocument, property: string): string | number {
      switch(property){
        case 'edited': return item.date?.getTime() ?? 0;
        case 'name': return item.name ?? '';
        case 'description': return item.description ?? '';
        default: {
          const value = (item as unknown as Record<string, unknown>)[property];
          return typeof value === 'string' || typeof value === 'number' ? value : '';
        }
      }
    }

    private processAuctionId(){
      if(this.auctionId()){
        this.propertyService.getLegalAdForAuction(this.auctionId()!).pipe(take(1)).subscribe(la => {
          if(la?.id>0){
            let dataSource;
            if(!this.groupedByContext()){
              if(!this.dataSources!.get('all')){
                this.dataSources!.set('all', new MatTableDataSource<AbstractDocument>([]));
              }
              dataSource = this.dataSources!.get('all');
            }
            else{
              const groupingName = la.legalAdAuctions?.find(laa=>laa.auctionId==this.auctionId())?.propertyHeader ?? '';
              if(!this.dataSources!.get(groupingName)){
                this.dataSources!.set(groupingName, new MatTableDataSource<AbstractDocument>([]));
              }
              dataSource = this.dataSources!.get(groupingName);
            }
            let ds = dataSource!.data.slice(0); //array could already be created vault document processing
            let ad = new AbstractDocument();
            ad.id=la.id;
            ad.name="Legal Ad";
            ad.description=la.county;
            ad.typeId=2;
            ad.date=la.createdAt;
            ad.isLegalAd=true;
            ad.contentType="text/pdf";
            ad.visibility=9;
            ad.documentReference=la.documentReference;
            ds.push(ad);
            dataSource!.data=ds;
          }
        });
      }
    }

    public documentIsRestricted(ad:AbstractDocument){
      return !this.userCanSeeRestrictedDocuments() && ad.visibility==2;
    }
    
    public onViewDocument(ad:AbstractDocument){
      if(this.documentIsRestricted(ad)){
        this.executeNDA.emit(undefined);
      }
      else{
        if(ad.typeId!=2){
          //regular vault document; the property it belongs to lets the server check the document is visible to client users
          const vd = this.vaultDocuments()?.find(v=>v.id==ad.id);
          const propertyId = vd?.context=='property' ? vd.contextId : undefined;
          this.propertyService.getVaultFile(ad.id, propertyId).pipe(take(1)).subscribe(r=>{
              base64ToBlob(r.base64File, ad.contentType).then(blob => {
              const blobUrl = URL.createObjectURL(blob);
              window.open(blobUrl, '_blank');
            });
          });
        }
        else{
          //legal ad document
          if(this.readOnly()){
            this.propertyService.downloadData(ad.documentReference).pipe(take(1)).subscribe(blob=>{ 
              const blobUrl = URL.createObjectURL(blob);
              window.open(blobUrl, '_blank');
            });
          }
          else{
            this.showLegalAd.emit(undefined);
          }
        }
      }
    }

    public onDeleteDocument(ad:AbstractDocument){
      const vd = this.vaultDocuments()?.find(vd=>vd.id==ad.id)
      if(vd) this.deleteDocument.emit(vd);
    }

    public onEditDocument(ad:AbstractDocument){
      const vd = this.vaultDocuments()?.find(vd=>vd.id==ad.id)
      if(vd) this.editDocument.emit(vd);
    }

    public getDocumentType(ad:AbstractDocument){
      return this.generalDataService.getDocumentTypeById(ad.typeId);
    }

    public noEdit(ad:AbstractDocument){
      return ad.typeId==2; //don't allow deleting legalAd from here => seperate workflow, to open legal ad first and then void from there.
    } 
  }

